import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { creditsFromUsage } from "@/lib/credit-pricing";
import { getSessionUser } from "@/lib/auth-session";

export interface CreditBreakdown {
  total: number;
  text: number;
  voice: number;
  image: number;
  serp: number;
  proxy: number;
}

const EMPTY: CreditBreakdown = { total: 0, text: 0, voice: 0, image: 0, serp: 0, proxy: 0 };

/**
 * Returns the total credits a user has spent on a specific world or adventure,
 * broken down by cost type (text, voice, image, serp, proxy).
 * Queries usage_logs where metadata contains the given world_id or adventure_id.
 */
export function useResourceCredits(
  resourceType: "world" | "adventure",
  resourceId: string | null,
) {
  const [credits, setCredits] = useState<CreditBreakdown>(EMPTY);
  const [loading, setLoading] = useState(false);
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    if (!resourceId) return;
    cancelledRef.current = false;
    setLoading(true);
    try {
      const user = await getSessionUser();
      if (cancelledRef.current) return;
      if (!user) { setLoading(false); return; }

      // Use server-side aggregation RPC (get_resource_credit_breakdown) instead of
      // pulling every usage_log row into the browser.  The RPC groups by cost_type
      // and returns at most 5 rows; the credit calculation stays client-side.
      // The cast through `any` is needed until generated types include this RPC.
      const { data, error } = await (supabase as any).rpc(
        "get_resource_credit_breakdown",
        { _user_id: user.id, _resource_type: resourceType, _resource_id: resourceId },
      );

      if (cancelledRef.current) return;
      if (error) { console.warn("useResourceCredits:", error.message); setLoading(false); return; }

      const breakdown: CreditBreakdown = { total: 0, text: 0, voice: 0, image: 0, serp: 0, proxy: 0 };

      for (const row of (data as any[]) || []) {
        const costType = (row.cost_type as string) || "text";
        // Aggregated totals from the DB — apply the same creditsFromUsage logic.
        const cost = creditsFromUsage(
          costType === "text" || !["voice", "image", "serp", "proxy"].includes(costType) ? Number(row.input_tokens_sum  || 0) : 0,
          costType === "text" || !["voice", "image", "serp", "proxy"].includes(costType) ? Number(row.output_tokens_sum || 0) : 0,
          costType === "voice" ? Number(row.input_chars_sum || 0) : 0,
          costType === "image" ? Number(row.row_count || 0) : 0,
          costType === "serp"  ? Number(row.row_count || 0) : 0,
          costType === "proxy" ? Number(row.row_count || 0) : 0,
        );
        const bucket = (["text", "voice", "image", "serp", "proxy"].includes(costType) ? costType : "text") as keyof CreditBreakdown;
        (breakdown as any)[bucket] += cost;
        breakdown.total += cost;
      }

      // Round all values
      for (const key of Object.keys(breakdown) as (keyof CreditBreakdown)[]) {
        breakdown[key] = Math.round(breakdown[key] * 10000) / 10000;
      }

      setCredits(breakdown);
    } catch (err) {
      if (!cancelledRef.current) console.warn("useResourceCredits error:", err);
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [resourceType, resourceId]);

  useEffect(() => {
    cancelledRef.current = false;
    load();
    return () => { cancelledRef.current = true; };
  }, [load]);

  return { credits, loading, reload: load };
}

/**
 * Server-side aggregated credit usage for the current user. Replaces the old
 * client-side per-resource fan-out by calling the get_resource_usage_summary RPC,
 * which returns { worlds: { id: credits }, adventures: { id: credits } } in a
 * single round trip.
 *
 * Returns the full summary plus a `loading` flag. Callers typically pick the
 * resource type they care about (see useBatchResourceCredits below).
 */
interface UsageSummary {
  worlds: Record<string, number>;
  adventures: Record<string, number>;
}

const EMPTY_SUMMARY: UsageSummary = { worlds: {}, adventures: {} };

export function useResourceUsageSummary() {
  const [summary, setSummary] = useState<UsageSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const user = await getSessionUser();
        if (!user) { if (!cancelled) setLoading(false); return; }

        // The RPC isn't in the generated types yet; cast through `any`.
        const { data, error } = await (supabase as any).rpc(
          "get_resource_usage_summary",
          { _user_id: user.id },
        );
        if (cancelled) return;
        if (error || !data) {
          if (error) console.warn("get_resource_usage_summary:", error.message);
          setLoading(false);
          return;
        }

        // Supabase returns numeric values as strings in some configurations;
        // coerce defensively so consumers get plain numbers.
        const coerce = (m: Record<string, unknown> | null | undefined): Record<string, number> => {
          const out: Record<string, number> = {};
          for (const [k, v] of Object.entries(m || {})) out[k] = Number(v) || 0;
          return out;
        };

        setSummary({
          worlds:     coerce((data as any).worlds),
          adventures: coerce((data as any).adventures),
        });
      } catch (err) {
        if (!cancelled) console.warn("useResourceUsageSummary error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return { summary, loading };
}

/**
 * Batch-fetch credit totals for multiple worlds or adventures at once.
 * Returns a map of resourceId -> total credits.
 *
 * Backed by the get_resource_usage_summary RPC, so a single network call covers
 * every resource the caller cares about (and any others, harmlessly). The
 * `resourceIds` argument is kept for backward compatibility and is used only to
 * filter the returned map down to the requested ids.
 */
export function useBatchResourceCredits(
  resourceType: "world" | "adventure",
  resourceIds: string[],
) {
  const { summary, loading } = useResourceUsageSummary();

  const creditsMap = useMemo(() => {
    const source = resourceType === "world" ? summary.worlds : summary.adventures;
    if (resourceIds.length === 0) return {};
    const out: Record<string, number> = {};
    for (const id of resourceIds) {
      if (source[id] !== undefined) out[id] = source[id];
    }
    return out;
  // P-20: sort before join so reordering the same IDs doesn't bust the memo
  }, [summary, resourceType, [...resourceIds].sort().join(",")]);

  return { creditsMap, loading };
}

/** Format credits for display */
export function formatCredits(amount: number): string {
  if (amount === 0) return "0";
  if (amount < 0.01) return "<0.01";
  if (amount < 1) return amount.toFixed(2);
  return amount.toFixed(1);
}
